import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, Button } from "@mui/material";
import useWebInvoicePrint from "helpers/InvoiceHelper";
import {
  INVOICE_FORMATS,
  KEYBOARD_KEYS,
  SALE_SYNC_STATUS,
  ROUTES,
  SYSTEM_VIEW,
  EVENTS,
  STAFF_PERMISSION,
  SALE_INVOICE_TITLES,
  SALE_STATUS,
} from "const";
import { eventsEmitter } from "utils/events";
import { amountRoundOff } from "helpers/NumberFormatHelper";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./styles.module.css";
import ShopContext from "context/ShopContext";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import A4Invoice from "components/utils/PrintInvoice/A4Invoice";
import A5Invoice from "components/utils/PrintInvoice/A5Invoice";
import ThreeInchInvoice from "components/utils/PrintInvoice/ThreeInchInvoice";
import toast from "react-hot-toast";
import * as SalesHelper from "helpers/SalesHelper";
import * as StringHelper from "helpers/StringHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as AppSettingsHelper from "helpers/AppSettingsHelper";
import SalesContext from "context/SalesContext";
import { usePermission } from "hooks/usePermission";
import SaleEditDialog from "../SaleEditDialog/SaleEditDialog";
import ConfirmPopup from "components/popups/Alert/ConfirmPopup";
import { ReactComponent as EditIcon } from "images/svg/edit.svg";
import * as InvoiceActionHelper from "helpers/InvoiceActionHelper";
import * as EditedSaleHelper from "helpers/EditedSaleHelper";

export default function SaleDetailsDialog({ open, onClose, saleId }) {
  const navigate = useNavigate();
  const { selectedShop } = useContext(ShopContext);
  const { getSaleById, setActiveSale, refreshSales } = useContext(SalesContext);
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reprintTitles, setReprintTitles] = useState([]);
  const { checkPermissionAndExecute, PermissionDialog } = usePermission();
  const [isEditPaymentOpen, setIsEditPaymentOpen] = useState(false);
  const [isSaleUpdated, setIsSaleUpdated] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const printComponentRef = useRef(null);

  const fetchSaleDetails = useCallback(async () => {
    setLoading(true);
    try {
      const saleData = await getSaleById(saleId);
      if (saleData) {
        setSale(saleData);
      } else {
        setSale(null);
      }
    } catch (error) {
      console.error("Error fetching sale details:", error);
    } finally {
      setLoading(false);
    }
  }, [getSaleById, saleId]);

  useEffect(() => {
    if (open && saleId) {
      fetchSaleDetails();
    }
  }, [open, saleId, fetchSaleDetails]);

  const handleWebInvoicePrint = useWebInvoicePrint({
    contentRef: printComponentRef,
    documentTitle: `Invoice-${sale?.bill_no || "NA"}`,
  });

  const onPrintClick = useCallback(() => {
    const proceedToPrint = async () => {
      try {
        setReprintTitles([SALE_INVOICE_TITLES.DUPLICATE]);
        await SalesHelper.saveAndProcess(sale, {
          shop: selectedShop,
          fallbackPrintFunction: handleWebInvoicePrint,
          titles: [SALE_INVOICE_TITLES.DUPLICATE],
        });
      } catch (error) {
        if (error.message.includes("nodevicefound")) {
          toast.error("No printer found! Added to network queue.");
        } else {
          console.error(error?.message || "Failed to print invoice.");
        }
      }
    };

    checkPermissionAndExecute(STAFF_PERMISSION.REPRINT, proceedToPrint, "Reprint Access Required");
  }, [sale, selectedShop, handleWebInvoicePrint, checkPermissionAndExecute]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open || !sale) return;

      if (event.key === KEYBOARD_KEYS.F4) {
        event.preventDefault();
        onPrintClick();
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.KEY_DOWN, handleKeyDown);
    return unsubscribeFn;
  }, [open, sale, onPrintClick]);

  const onEditClick = async () => {
    if (!sale) return;

    // Check if a working draft copy already exists for this sale
    const existingDraft = await SalesHelper.getSaleByEditingSaleLocalId(sale.local_id);
    if (existingDraft) {
      toast.error("Sale is already in editing");
      return;
    }

    checkPermissionAndExecute(STAFF_PERMISSION.SALE_EDIT, () => proceedToEdit(), "Edit Access Required");
  };

  const proceedToEdit = async () => {
    setLoading(true);
    try {
      const { bill_no, bare_bill_no, local_id, id, ...saleData } = sale;
      const draftSale = {
        ...saleData,
        editing_sale_local_id: sale.local_id,
        original_sale_bill_no: sale.bill_no,
        previous_payable: sale.payable,
        status: SALE_STATUS.DRAFT,
      };

      const tempLocalId = await SalesHelper.saveSaleInDB(draftSale, { notify: true });
      const draftToUse = { ...draftSale, local_id: tempLocalId };
      await refreshSales();
      setActiveSale(draftToUse);
      onClose();

      const terminalSettings = TerminalHelper.getTerminalSettings();
      if (terminalSettings?.systemType === SYSTEM_VIEW.ERP) {
        navigate(ROUTES.ERP_DASHBOARD);
      } else {
        navigate(ROUTES.SALES);
      }
    } catch (err) {
      console.error("proceedToEdit error", err);
      toast.error("Failed to initiate edit");
    } finally {
      setLoading(false);
    }
  };

  const invoiceFormat = SalesHelper.getInvoicePrinterFormat();

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose(isSaleUpdated);
    }
  };

  const onResetBillNoClick = () => {
    if (!sale) return;
    checkPermissionAndExecute(STAFF_PERMISSION.SALE_EDIT, () => setShowResetConfirm(true), "Edit Access Required");
  };

  const handleConfirmResetBillNo = async () => {
    setShowResetConfirm(false);
    if (!sale) return;

    try {
      setLoading(true);
      const { bill_no, bare_bill_no } = await AppSettingsHelper.getNewBillNumber();

      const updatedSale = {
        ...sale,
        bill_no,
        bare_bill_no,
      };

      await SalesHelper.saveSaleInDB(updatedSale, { notify: true });
      await AppSettingsHelper.saveNextBillNumber(bare_bill_no + 1);

      setSale(updatedSale);
      setIsSaleUpdated(true);
      toast.success(`Bill number reset to ${bill_no}`);
      onClose(true);
    } catch (err) {
      console.error("Error resetting bill number:", err);
      toast.error("Failed to reset bill number");
    } finally {
      setLoading(false);
    }
  };

  const handleShareWhatsApp = () => {
    try {
      InvoiceActionHelper.shareInvoiceOnWhatsApp(sale, selectedShop);
    } catch (error) {
      toast.error(error?.message || "WhatsApp share failed: Customer mobile missing.");
    }
  };

  return (
    <div className={styles.popUp} onClick={handleBackdropClick}>
      <Grid container className={styles.billContentWrapper} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Grid item xs={12} className={styles.headSec} mb={2}>
          <h3>Sale Details</h3>
          <CloseIcon className={styles.icon} onClick={() => onClose(isSaleUpdated)} />
        </Grid>

        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", width: "100%", color: "#666" }}>
            Loading sale details...
          </div>
        ) : !sale ? (
          <div style={{ padding: "40px", textAlign: "center", width: "100%", color: "#666" }}>
            Sale details not found for ID: <strong>{saleId}</strong>
          </div>
        ) : (
          <>
            {/* Meta info section */}
            <Grid item xs={12} className={styles.headInfoSec}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Table</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      {sale.table?.name
                        ? `${sale.table.name}${sale.table.chair?.name ? ` (${sale.table.chair.name})` : ""}`
                        : "-"}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Bill Number</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{sale.bill_no}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Order Type</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{sale?.order_type || "-"}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Customer</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{sale?.customer?.name || "-"}</div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Items</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>{sale.items?.length || 0}</div>
                  </div>
                </Grid>

                <Grid item xs={4}>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Discount</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff((sale?.discount_applied_on_items || 0) + (sale?.discount || 0))}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Payable</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(sale.payable)}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Pre Paid</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(sale.prepaid || 0)}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Due Amount</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountRed}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(sale.due || 0)}
                    </div>
                  </div>
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>RoundOff Amount</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                      {amountRoundOff(sale.roundOff)}
                    </div>
                  </div>
                </Grid>

                <Grid item xs={4}>
                  {sale?.selectedChargeGroups?.length > 0 && (
                    <div className={styles.priceTxt}>
                      <div className={styles.subTitle}>Charge Group</div>
                      <div className={styles.colon}>:</div>
                      <div className={styles.amountGray} style={{ wordBreak: "break-word" }}>
                        {sale.selectedChargeGroups
                          .map((group) => group?.name)
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </div>
                    </div>
                  )}
                  <div className={styles.priceTxt}>
                    <div className={styles.subTitle}>Date & Time</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.amountGray}>
                      {sale.time ? DateTimeHelpers.formatDateTime(sale.time) : sale.dateTime || "-"}
                    </div>
                  </div>
                  <div className={styles.statusContainer}>
                    <div className={styles.statusLabel}>Sync Status</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.statusValue}>{StringHelper.formatLabel(sale.sync_status)}</div>
                  </div>

                  <div className={styles.statusContainer}>
                    <div className={styles.statusLabel}>Sale Status</div>
                    <div className={styles.colon}>:</div>
                    <div className={styles.statusValue}>{StringHelper.formatLabel(sale.status)}</div>
                  </div>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} className={styles.headInfoSec}>
              {sale.remark && (
                <div className={styles.priceTxt} style={{ alignItems: "flex-start" }}>
                  <div className={styles.subTitle}>Remark</div>
                  <div className={styles.colon}>:</div>
                  <div className={styles.amountGray} style={{ wordBreak: "break-word", flexDirection: "column" }}>
                    {EditedSaleHelper.splitRemarkLines(sale.remark).length > 0 ? (
                      EditedSaleHelper.splitRemarkLines(sale.remark).map((line, idx) => <div key={idx}>{line}</div>)
                    ) : (
                      <div>{sale.remark}</div>
                    )}
                  </div>
                </div>
              )}
            </Grid>

            {/* Items Table */}
            <Grid item xs={12} className={styles.tableContainer}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ width: "10%" }}>No</th>
                    <th style={{ width: "45%" }}>Items</th>
                    <th style={{ width: "15%", textAlign: "right" }}>Rate</th>
                    <th style={{ width: "15%", textAlign: "center" }}>Qty</th>
                    <th style={{ width: "15%", textAlign: "right" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(sale.items || []).map((item, index) => {
                    const qty = item.count ?? item.qty ?? 1;
                    const rate = item.rateAndTax ?? 0;
                    const itemTotal = item.payable ?? qty * rate;

                    return (
                      <tr key={index}>
                        <td style={{ width: "10%" }}>{index + 1}</td>
                        <td style={{ width: "45%" }}>
                          <div>
                            {item.name} {item.option?.label ? ` - ${item.option.label}` : ""}
                          </div>
                          {item.note ? (
                            <div style={{ fontSize: "11px", color: "#666", marginTop: "2px", fontStyle: "italic" }}>
                              Note: {item.note}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ width: "15%", textAlign: "right" }}>{amountRoundOff(rate)}</td>
                        <td style={{ width: "15%", textAlign: "center" }}>{qty}</td>
                        <td style={{ width: "15%", textAlign: "right" }}>{amountRoundOff(itemTotal)}</td>
                      </tr>
                    );
                  })}
                  {(!sale.items || sale.items.length === 0) && (
                    <tr>
                      <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                        No items found in this sale
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className={styles.tfootRow}>
                  <tr className={styles.tfootTr}>
                    <td colSpan="2" className={styles.tfootLabel}>
                      Total
                    </td>
                    <td className={styles.tfootRate}></td>
                    <td className={styles.tfootQty}>
                      {(sale?.items || []).reduce((acc, it) => acc + Number(it.count ?? it.qty ?? 1), 0)}
                    </td>
                    <td className={styles.tfootTotal}>
                      {(sale?.items || []).reduce((acc, it) => acc + Number(it.payable ?? 1), 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </Grid>

            <div className={styles.border}></div>

            <div className={styles.printSection}>
              <h3 className={styles.tableHeadText}>Payment Modes</h3>
              <div className={styles.paymentModesContainer}>
                <div className={styles.paymentModesList}>
                  {(sale.payment_modes || []).map((pm, index) => (
                    <div key={index} className={styles.paymentModeItem}>
                      <div className={styles.modeName}>{pm.name}</div>
                      <div className={styles.modeAmount}>{amountRoundOff(pm.amount)}</div>
                    </div>
                  ))}
                  {Number(sale?.customer_credit_used || 0) !== 0 && (
                    <div className={styles.paymentModeItem}>
                      <div className={styles.modeName}>Customer Credit</div>
                      <div className={styles.modeAmount}>{sale.customer_credit_used}</div>
                    </div>
                  )}
                  {Number(sale?.loyalty_points_amount) !== 0 && (
                    <div className={styles.paymentModeItem}>
                      <div className={styles.modeName}>Loyalty Point</div>
                      <div className={styles.modeAmount}>{sale.loyalty_points_amount}</div>
                    </div>
                  )}
                </div>

                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    checkPermissionAndExecute(
                      STAFF_PERMISSION.SALE_EDIT,
                      () => setIsEditPaymentOpen(true),
                      "Edit Payment Access",
                    );
                  }}
                  className={styles.editPaymentBtn}
                >
                  <EditIcon className={styles.icon} />
                  Edit payment modes
                </Button>
              </div>
            </div>

            <div className={styles.border}></div>

            <Grid item xs={12} className={styles.footSec} mt={2}>
              {sale?.sync_status !== SALE_SYNC_STATUS.SYNCED_TO_CLOUD && (
                <Button variant="outlined" className={styles.buttonGray} onClick={onResetBillNoClick}>
                  Reset Bill No
                </Button>
              )}
              <Button variant="outlined" className={styles.buttonGray} onClick={onPrintClick}>
                Re Print
              </Button>
              <Button variant="outlined" className={styles.buttonGray} onClick={onEditClick}>
                Edit Sale
              </Button>
              <Button variant="outlined" className={styles.buttonGray} onClick={handleShareWhatsApp}>
                Share
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      <div
        style={{
          position: "absolute",
          left: "-10000px",
          top: "-10000px",
          visibility: "hidden",
        }}
      >
        {invoiceFormat === INVOICE_FORMATS.A4 ? (
          <A4Invoice ref={printComponentRef} sale={sale} shop={selectedShop} titles={reprintTitles} />
        ) : invoiceFormat === INVOICE_FORMATS.A5 ? (
          <A5Invoice ref={printComponentRef} sale={sale} shop={selectedShop} titles={reprintTitles} />
        ) : (
          <ThreeInchInvoice ref={printComponentRef} sale={sale} shop={selectedShop} titles={reprintTitles} />
        )}
      </div>
      <PermissionDialog />
      <ConfirmPopup
        data={showResetConfirm}
        handleClose={() => setShowResetConfirm(false)}
        handleOkay={handleConfirmResetBillNo}
        alertTitle="Confirm reset bill number"
        alertContent="Are you sure you want to reset the bill number for this sale?"
        successBtnName="Confirm"
      />
      {isEditPaymentOpen && (
        <SaleEditDialog
          saleLocalId={sale.local_id}
          isEditingPaymentModeOnly={true}
          handleClose={() => {
            setIsEditPaymentOpen(false);
            fetchSaleDetails();
          }}
          onDone={() => {
            setIsEditPaymentOpen(false);
            setIsSaleUpdated(true);
            fetchSaleDetails();
          }}
        />
      )}
    </div>
  );
}
